import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "./HelpPopUp.css";
import { post } from "../helper/APIClient";
import { Routes } from "../helper/Routes";
import { useChatWidget } from "../context/useChatWidget";

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  text: string;
}

const makeMessage = (role: ChatMessage["role"], text: string): ChatMessage => ({
  id: `${Date.now()}-${Math.random()}`,
  role,
  text,
});

const GREETING_TEXT =
  "Hi! I'm the FamilySafe assistant. I can help you understand how to use FamilySafe, what to store, and how features like sharing, probate, and emergency access work. What would you like to know?";

const SUGGESTIONS = [
  "How do I add a card?",
  "What should I store for my car?",
  "How does probate work?",
  "Is my data encrypted?",
];

const STORAGE_KEY = "familysafe.chat.messages";

const loadStoredMessages = (): ChatMessage[] => {
  if (globalThis.window === undefined) return [makeMessage("assistant", GREETING_TEXT)];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [makeMessage("assistant", GREETING_TEXT)];
    const parsed = JSON.parse(raw) as ChatMessage[];
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {
    // ignore corrupt storage and fall through to greeting
  }
  return [makeMessage("assistant", GREETING_TEXT)];
};

const HelpPopup: React.FC = () => {
  const { isExpanded, collapse, pendingMessage, clearPendingMessage } = useChatWidget();
  const location = useLocation();
  const [messages, setMessages] = useState<ChatMessage[]>(loadStoredMessages);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {
      // sessionStorage may be unavailable (private browsing) — silently skip
    }
  }, [messages]);

  useEffect(() => {
    if (isExpanded) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      inputRef.current?.focus();
    }
  }, [isExpanded, messages, loading]);

  const sendMessage = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, makeMessage("user", trimmed)]);
    setInput("");
    setLoading(true);

    try {
      const data = await post<{ reply: string }>(Routes.ASSISTANT_CHAT, {
        message: trimmed,
        pagePath: location.pathname,
      });
      setMessages((prev) => [...prev, makeMessage("assistant", data.reply)]);
    } catch {
      setMessages((prev) => [
        ...prev,
        makeMessage(
          "assistant",
          "Sorry, I couldn't reach the assistant right now. Please try again shortly."
        ),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    sendMessage(input);
  };

  useEffect(() => {
    if (isExpanded && pendingMessage) {
      sendMessage(pendingMessage);
      clearPendingMessage();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, pendingMessage]);

  if (!isExpanded) {
    return null;
  }

  return (
    <div className="fs-chat-widget" role="dialog" aria-label="FamilySafe assistant">
      <div className="fs-chat-header">
        <div className="fs-chat-header-text">
          <span className="fs-chat-title">Ask FamilySafe</span>
          <span className="fs-chat-subtitle">Powered by FamilySafe AI</span>
        </div>
        <div className="fs-chat-header-actions">
          <button
            type="button"
            className="fs-chat-header-btn"
            onClick={collapse}
            aria-label="Minimise chat"
            title="Minimise"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <button
            type="button"
            className="fs-chat-header-btn"
            onClick={collapse}
            aria-label="Close chat"
            title="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
      </div>

      <div className="fs-chat-messages">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`fs-chat-bubble-msg fs-chat-bubble-msg--${msg.role}`}
          >
            {msg.text}
          </div>
        ))}

        {loading && (
          <div className="fs-chat-bubble-msg fs-chat-bubble-msg--assistant fs-chat-typing">
            <span />
            <span />
            <span />
          </div>
        )}

        {messages.length === 1 && !loading && (
          <div className="fs-chat-suggestions">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className="fs-chat-suggestion"
                onClick={() => sendMessage(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <form className="fs-chat-input-row" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          className="fs-chat-input"
          type="text"
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={1000}
          disabled={loading}
        />
        <button
          className="fs-chat-send"
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Send"
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default HelpPopup;
